import { useState, useRef } from 'react';
import { Button, Input } from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Link } from 'wouter';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import useBrand from 'common/src/hooks/useBrand';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const turnstileRef = useRef<TurnstileInstance>(null);
  const { assets: brandAssets } = useBrand();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }

    if (!captchaToken) {
      toast.error('Por favor completa la verificación de seguridad');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            captchaToken,
            redirectTo: `${window.location.origin}/reset-password`,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Error al solicitar restablecimiento de contraseña');
        turnstileRef.current?.reset();
        setCaptchaToken(undefined);
        return;
      }

      setEmailSent(true);
      toast.success('Te hemos enviado un email con las instrucciones para restablecer tu contraseña');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Error al solicitar restablecimiento de contraseña');
      turnstileRef.current?.reset();
      setCaptchaToken(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='relative flex h-screen w-screen bg-background'>
      {/* Brand Logo */}
      <div className='absolute left-2 top-5 left-4 lg:left-5'>
        <div className='flex items-center'>
          <img src={brandAssets.fullLogoLight} alt={brandAssets.name} className='h-8 dark:hidden' />
          <img src={brandAssets.fullLogoDark} alt={brandAssets.name} className='h-8 hidden dark:block' />
        </div>
      </div>

      {/* Forgot Password Form */}
      <div className='flex w-full items-center justify-center lg:w-1/2'>
        <div className='w-full max-w-md p-8 bg-background rounded-lg shadow-md mx-4'>
          {!emailSent ? (
            <>
              {/* design-ignore: pantalla de recuperar contraseña (pública, sin Header) */}
              <h2 className='text-2xl font-semibold mb-2'>¿Olvidaste tu contraseña?</h2>
              <p className='text-sm text-default-500 mb-6'>
                Ingresa tu email y te enviaremos las instrucciones para restablecer tu contraseña
              </p>
              <form onSubmit={handleResetPassword} className='flex flex-col gap-4'>
                <Input
                  type='email'
                  label='Email'
                  placeholder='tu@email.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isRequired
                />

                {/* Cloudflare Turnstile */}
                <div className='flex justify-center'>
                  <Turnstile
                    ref={turnstileRef}
                    siteKey='0x4AAAAAACKyDNxQSOYs1Pwy'
                    onSuccess={(token) => {
                      setCaptchaToken(token);
                    }}
                    onError={() => {
                      setCaptchaToken(undefined);
                      toast.error('Error en la verificación de seguridad. Intenta recargar la página.');
                    }}
                    onExpire={() => {
                      setCaptchaToken(undefined);
                    }}
                  />
                </div>

                <Button
                  type='submit'
                  color='primary'
                  className='w-full'
                  isLoading={loading}
                  isDisabled={!captchaToken}
                >
                  Enviar instrucciones
                </Button>
                <div className='text-center'>
                  <Link href='/login'>
                    <a className='text-sm text-brand-900 hover:underline'>
                      Volver al inicio de sesión
                    </a>
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className='flex flex-col items-center gap-4'>
                <div className='rounded-full bg-default-100 p-3'>
                  <svg
                    className='h-12 w-12 text-green-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                    />
                  </svg>
                </div>
                {/* design-ignore: pantalla de recuperar contraseña (pública, sin Header) */}
                <h2 className='text-2xl font-semibold text-center'>Revisa tu email</h2>
                <p className='text-sm text-default-500 text-center'>
                  Hemos enviado las instrucciones para restablecer tu contraseña a <strong>{email}</strong>
                </p>
                <p className='text-xs text-default-400 text-center'>
                  Si no recibes el email en unos minutos, revisa tu carpeta de spam
                </p>
                <Link href='/login'>
                  <Button
                    color='primary'
                    className='w-full mt-4'
                  >
                    Volver al inicio de sesión
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right side */}
      <div
        className='relative hidden w-1/2 flex-col-reverse rounded-medium shadow-small lg:flex'
        style={{
          backgroundImage: 'url(/cherry.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    </div>
  );
};

export default ForgotPassword;
